import React from 'react'
import {
  Title,
  SingleLineText,
  MultiLineText,
  Url,
  RichTextEditor,
  Markdown,
  SelectField,
  ModularBlocks,
  Number,
  Boolean,
  DateFieldProp,
  File,
  Group,
  Link,
  Global,
  Custom,
  Reference,
  JsonRte,
  ExperienceContainer,
  Taxonomy
} from './ContentTypeFieldProperties'
import { getTestIdFormatter } from '../../utils/helpers/index'
import Field from '../Field/Field'
import FieldLabel from '../FieldLabel/FieldLabel'
import Tooltip from '../Tooltip/Tooltip'
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch'
import Button from '../Button/Button'
import ButtonGroup from '../Button/ButtonGroup/ButtonGroup'
import Checkbox from '../Checkbox/Checkbox'
import { ModalBody, ModalHeader, ModalFooter } from '../Modal/Modal'
import Icon from '../Icon2/Icon'
import cbModal from '../Modal/Modal'
import './ContentTypeField.css';
import Help from '../Help/Help';

const NonLocalizableToggleSwitch = ({ uniqueKey, schema, onOptionsChange, dataTestId, dataTestIdForDescription, isNonLocalizable, userCustomFieldLimits = { hasUserCustomFieldLimit: false, hasUserCustomFieldLimitRestFields: false }, isAncestorNonLoc }) => {
  const shouldDisableToggle = isNonLocalizable || isAncestorNonLoc
  const { hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields } = userCustomFieldLimits
  return (
    <div className={`${hasUserCustomFieldLimit || hasUserCustomFieldLimitRestFields ? 'flex-v-center' : ''}`}>
      <div className={`ToggleWrap ${hasUserCustomFieldLimit || hasUserCustomFieldLimitRestFields ? 'flex-v-center' : ''}`}>
        {isNonLocalizable ? (
          <Tooltip position="top" content={isNonLocalizable ?`Available only if there are multiple languages in your stack.` : `Disabled as the parent field is set to Non-localizable.`}>
            <ToggleSwitch
              testId={dataTestId}
              name={`${uniqueKey}.nonLocalization`}
              label="Non-localizable"
              checked={schema.non_localizable}
              disabled={shouldDisableToggle}
              onChange={onOptionsChange}
            />
          </Tooltip>
        ) : (
          <ToggleSwitch
            testId={dataTestId}
            name={`${uniqueKey}.nonLocalization`}
            label="Non-localizable"
            checked={schema.non_localizable}
            disabled={shouldDisableToggle}
            onChange={onOptionsChange}
          />
        )}
      </div>
      {hasUserCustomFieldLimit || hasUserCustomFieldLimitRestFields ?
      <div>
        <Help type="basic" data-test-id={dataTestIdForDescription} className='mb-0' iconName='Information' text="If enabled, editing this field is restricted in localized entries. The field will use the value of
        the master-language entry in all localized entries." />
      </div>
      :
      <div className="nl-note mt-8" data-test-id={dataTestIdForDescription}>
        If enabled, editing this field is restricted in localized entries. The field will use the value of
        the master-language entry in all localized entries.
      </div>
    }
    </div>
  )
}

const getModalDesciption = (fieldDataType, toggleType, t) => {
  if (toggleType === 'nonLocalizable') {
    if (fieldDataType === 'group') {
      return (
        <>
          <p>
            You are about to set this field as non-localizable within a Group field marked as 'Multiple'.
          </p>
          <p>
            Please note that if editors create a new, individual instance of this Group (for which no corresponding instance is available in master entry) in the localized entry, the non-localizable field will be editable in localized entries.
          </p>
          <p>
            We recommend reading about how this exception works before proceeding.{' '}
            <a
              href={t('content_type.save_content_type.non_loc_doc_learn_more')}
              target="_blank"
            >
              Learn more.
            </a>
          </p>
        </>
      )
    }
    if (fieldDataType === 'blocks') {
      return (
        <>
          <p>
            You are about to set this field as non-localizable within a Modular Block field (which is 'Multiple' by nature).
          </p>
          <p>
            Please note that if editors create a new, individual instance of this block (for which no corresponding instance is available in master entry) in the localized entry, the non-localizable field will be editable in localized entries.
          </p>
          <p>
            We recommend reading about how this exception works before proceeding.{' '}
            <a
              href={t('content_type.save_content_type.non_loc_doc_learn_more')}
              target="_blank"
            >
              Learn more.
            </a>
          </p>
        </>
      )
    }
  }
  if (toggleType === 'multiple') {
    if (fieldDataType === 'group') {
      return (
        <>
          <p>
            You are about to set this Group field as Multiple. One or more fields within the group field are marked as 'Non-localizable'.
          </p>
          <p>
            Please note that if editors create a new, individual instance of this Group (for which no corresponding instance is available in master entry) in the localized entry, the non-localizable field will be editable in localized entries.
          </p>
          <p>
            We recommend reading about how this exception works before proceeding.{' '}
            <a
              href={t('content_type.save_content_type.non_loc_doc_learn_more')}
              target="_blank"
            >
              Learn more.
            </a>
          </p>
        </>
      )
    }
    if (fieldDataType === 'global_field') {
      return (
        <>
          <p>
            You are about to set this Global field as Multiple. One or more fields within the Global field are marked as 'Non-localizable'.
          </p>
          <p>
            Please note that if editors create a new, individual instance of this Global (for which no corresponding instance is available in master entry) in the localized entry, the non-localizable field will be editable in localized entries.
          </p>
          <p>
            We recommend reading about how this exception works before proceeding.{' '}
            <a
              href={
                'https://www.contentstack.com/docs/developers/json-rich-text-editor/about-embedded-entries-or-assets-within-json-rte/'
              }
              target="_blank"
            >
              Learn more.
            </a>
          </p>
        </>
      )
    }
  }
  return <></>
}

const doNoShowCheckboxState = {
  non_loc_child_within_group: false,
  non_loc_child_within_blocks: false,
  multiple_group: false,
  multile_global_field: false
};

export const NonLocalizableModal = (props): JSX.Element => {
  const { fieldDataType, toggleType, ancestorWithMultipleType, t } = props;
  const modalBodySelector = toggleType === 'nonLocalizable' ? ancestorWithMultipleType : fieldDataType;
  return (
    <div className="non-localizable-modal" data-test-id="cs-non-localizable-modal">
      <ModalHeader title="Potential Non-Localizable Exception Scenario" closeModal={props.closeModal} />
      <ModalBody data-test-id="cs-non-localizable-modal-body">
        {
          getModalDesciption(modalBodySelector, toggleType, t)
        }
      </ModalBody>
      <ModalFooter>
        <ButtonGroup>
          <Button buttonType="tertiary" onClick={props.closeModal} testId="cs-non-localizable-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={(): void => {
              props.onContinue();
              props.closeModal();
            }}
            testId="cs-non-localizable-modal-continue"
          >
            Continue
          </Button>
        </ButtonGroup>
        <Checkbox
          label="Don’t show this again"
          onChange={(e): void => {
            if (toggleType === 'nonLocalizable' && ancestorWithMultipleType) {
              doNoShowCheckboxState[`non_loc_child_within_${ancestorWithMultipleType}`] = true
            }
            if (toggleType === 'multiple' && fieldDataType) {
              doNoShowCheckboxState[`multiple_${fieldDataType}`] = true
            }
            if (e.target.checked) localStorage.setItem('isNonLocalizableModalForCTHidden', JSON.stringify(doNoShowCheckboxState));
            // else localStorage.setItem('isNonLocalizableModalForCTHidden', JSON.stringify(doNoShowCheckboxState));
          }}
          testId="cs-non-localizable-modal-checkbox"
        />
      </ModalFooter>
    </div>
  );
};

export const NonLocalizableDom = ({ isFieldPresent, uniqueKey, schema, onOptionsChange, dataTestId, dataTestIdForDescription, isNonLocalizable, userCustomFieldLimits = { hasUserCustomFieldLimit: false, hasUserCustomFieldLimitRestFields: false }, isAncestorNonLoc }: {
  isFieldPresent: boolean;
  uniqueKey: string;
  schema: any;
  onOptionsChange: any;
  dataTestId: string;
  dataTestIdForDescription: string;
  isNonLocalizable?: boolean;
  userCustomFieldLimits?: {
    hasUserCustomFieldLimit: boolean;
    hasUserCustomFieldLimitRestFields: boolean;
  };
  isAncestorNonLoc?:boolean,
}) => {
  const { hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields } = userCustomFieldLimits
  return (
    !isFieldPresent ?
      <Field>
        <FieldLabel testId="cs-form-field-boolean-options" version="v2" htmlFor="options">Options</FieldLabel>
        <div className="FormFields__selection-wrapper">
          <NonLocalizableToggleSwitch
            uniqueKey={uniqueKey}
            schema={schema}
            onOptionsChange={onOptionsChange}
            dataTestId={dataTestId}
            dataTestIdForDescription={dataTestIdForDescription}
            isNonLocalizable={isNonLocalizable}
            userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
            isAncestorNonLoc={isAncestorNonLoc}
          />
        </div>
      </Field>
      : (<NonLocalizableToggleSwitch
        uniqueKey={uniqueKey}
        schema={schema}
        onOptionsChange={onOptionsChange}
        dataTestId={dataTestId}
        dataTestIdForDescription={dataTestIdForDescription}
        isNonLocalizable={isNonLocalizable}
        userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
        isAncestorNonLoc={isAncestorNonLoc}

      />)
  )
}

export const hasNonLocalizableField = (schema: any): boolean => {
  const checkNodeForNonLocalizableField = (node: any): boolean => {
    if (node.children) {
      return Array.from(node.children).some((child: any) => {
        if (child.non_localizable === true) {
          return true;
        }
        if (!child.multiple) {
          return checkNodeForNonLocalizableField(child);
        }
        return false;
      });
    }
    return false;
  };

  return checkNodeForNonLocalizableField(schema);
};

export const checkNodeForDuplicateChoice = (schema: any): boolean => {
  let hasDuplicate = false
    const seenValues = new Set()
    const seenKeys = new Set()
    
    for (let i = 0; i < schema.enum.choices.length; i++) {
      const choice = schema.enum.choices[i]
      
      if (schema.enum.advanced) {
        // For advanced mode, check both key and value duplicates
        if (choice.key && seenKeys.has(choice.key.toString().trim())) {
          hasDuplicate = true
          break
        }
        if (choice.value && seenValues.has(choice.value.toString().trim())) {
          hasDuplicate = true
          break
        }
        if (choice.key) seenKeys.add(choice.key.toString().trim())
        if (choice.value) seenValues.add(choice.value.toString().trim())
      } else {
        // For simple mode, only check value duplicates
        if (choice.value && seenValues.has(choice.value.toString().trim())) {
          hasDuplicate = true
          break
        }
        if (choice.value) seenValues.add(choice.value.toString().trim())
      }
    }
    return hasDuplicate
};

export const updateChildrenSChema = (schema: any, isChecked: boolean) => {
  if (schema.children && Array.isArray(schema.children) && schema.children.length) {
    schema.children.forEach((child: any) => {
      if (!child.multiple) {
        child.nonLocParams = {
          insideMultiple: (isChecked ? isChecked : schema.nonLocParams?.insideMultiple) || false,
          ancestorWithMultipleType: schema?.multiple ? schema?.data_type : schema.nonLocParams?.ancestorWithMultipleType
        }
        updateChildrenSChema(child, isChecked);
      } else {
        child.nonLocParams = {
          insideMultiple: (isChecked ? isChecked : schema.nonLocParams?.insideMultiple) || false,
          ancestorWithMultipleType: schema?.multiple ? schema?.data_type : schema.nonLocParams?.ancestorWithMultipleType
        }
      }
    });
  }
}

export const markNonLocalizableFalse = (node, isRoot = true) => {
  if (!node) return;

  if (Array.isArray(node)) {
    node.forEach(child => markNonLocalizableFalse(child, false));
    return;
  }

  if (typeof node === "object") {
    // Only unset if it's not the root node
    if (!isRoot && node.non_localizable === true) {
      node.non_localizable = false;
    }

    if (Array.isArray(node.schema)) {
      node.schema.forEach(child => markNonLocalizableFalse(child, false));
    }
    if (Array.isArray(node.children)) {
      node.children.forEach(child => markNonLocalizableFalse(child, false));
    }
  }
};


export const handleToggleClick = (event, fieldDataType, schema, onchange, toggleType, t , isInsideMultiple , ancestorWithMultipleType ? : string |null) => {
  // const ancestorWithMultipleType = schema?.nonLocParams?.ancestorWithMultipleType || '';
  const isDoNotShowChecked = JSON.parse(localStorage.getItem('isNonLocalizableModalForCTHidden'));
  const showNonLocExceptionModal = !isDoNotShowChecked?.[toggleType === 'nonLocalizable' ? `non_loc_child_within_${ancestorWithMultipleType}` : `multiple_${fieldDataType}`];
  let hasNonLocFieldExists = false;
  if (showNonLocExceptionModal) {
    if (toggleType === 'multiple') {
      hasNonLocFieldExists = hasNonLocalizableField(schema);
    } else if (toggleType === 'nonLocalizable') {
      hasNonLocFieldExists = isInsideMultiple || false;
    }
  }
  if (hasNonLocFieldExists) {
    cbModal({
      component: (props) =>
        <NonLocalizableModal
          {...props}
          t={t}
          fieldDataType={fieldDataType}
          ancestorWithMultipleType={ancestorWithMultipleType}
          toggleType={toggleType}
          onContinue={
            () => {
              toggleType === 'nonLocalizable' ? schema.non_localizable = true : schema.multiple = true;
              if(toggleType === 'nonLocalizable' && (schema.data_type === 'blocks' || 'group')){
                markNonLocalizableFalse(schema, true);
              }
              // if (toggleType === 'multiple') {
              //   updateChildrenSChema(schema, true);
              // }
              onchange();
            }
          } />,
      modalProps: {
        className: 'non-localizable-modal-parent'
      },
    })
  }
  else {
    if(toggleType === 'nonLocalizable' && ['blocks', 'group', 'global_field'].includes(schema.data_type)){
      handleMarkParentNonLocalizable(schema, onchange);
    }else{
      toggleType === 'nonLocalizable' ? schema.non_localizable = event.target.checked : schema.multiple = event.target.checked
    }
    // if (toggleType === 'multiple') {
    //   updateChildrenSChema(schema, true);
    // }
  }

}

export const fieldForm = (
  schema: any,
  options,
  parentInfo,
  uniqueKey = 'key.0',
  globalFieldOptions: any,
  extensionFieldOptions: any,
  referenceContentType: any,
  jsonRteContentType: any,
  rteContentType,
  handleChange: any,
  type: 'basic' | 'advanced',
  selectAsNumberText: any,
  selectAsKeyValue: any,
  selectAsSingle: any,
  selectOptionsLimit: any,
  currentFieldPath: any,
  enableEmbeddedObj: boolean,
  hasNonLocalizablePlan: boolean,
  isCTBuilder: boolean,
  t?: any,
  setOptions?: any,
  isNonLocalizable?: boolean,
  userCustomFieldLimits?: {
    hasUserCustomFieldLimit: boolean;
    hasUserCustomFieldLimitRestFields: boolean;
  },
  hasNonLocalizablePlanForBlocksAndGroupMultiple?: boolean,
  hasNonLocFieldsWithinGroupOrGlobalField?: boolean,
  hasTimePickerConfig?: boolean,
  isUserDefinedZoneEnabled?: boolean,
  contentTypeSchemaTree?: any,
  isGlobalFieldBuilderPage: boolean = false,
  urlManagementV2Visible?: boolean
) => {
  const changehndlr = () => {
    handleChange()
  }

  const setCTOptions = (options , actionFlag ?: string) => {
    setOptions(options, actionFlag)
  }

  const { hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields } = userCustomFieldLimits || { hasUserCustomFieldLimit: false, hasUserCustomFieldLimitRestFields: false }

  let fieldName = schema.fieldName || schema.field_name
  switch (fieldName) {
    case 'Title':
      return (
        <Title
          type={type}
          isCTBuilder={isCTBuilder}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
        />
      )
    case 'URL':
      return (
        <Url
          type={type}
          schema={schema}
          options={options}
          setOptions={setCTOptions}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          urlManagementV2Visible={urlManagementV2Visible}
          contentTypeSchemaTree={contentTypeSchemaTree}
        />
      )
    case 'Single Line Textbox':
      return (
        <SingleLineText
          type={type}
          isCTBuilder={isCTBuilder}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          options={options}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Multi Line Textbox':
      return (
        <MultiLineText
          type={type}
          isCTBuilder={isCTBuilder}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Rich Text Editor':
      return (
        <RichTextEditor
          type={type}
          enableEmbeddedObj={enableEmbeddedObj}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          options={options}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          {...rteContentType}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'JSON Rich Text Editor':
      return (
        <JsonRte
          currentFieldPath={currentFieldPath}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          enableEmbeddedObj={enableEmbeddedObj}
          type={type}
          options={options}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          {...jsonRteContentType}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Markdown':
      return (
        <Markdown
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Select':
      return (
        <SelectField
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          selectAsNumberText={selectAsNumberText}
          selectAsSingle={selectAsSingle}
          selectAsKeyValue={selectAsKeyValue}
          selectOptionsLimit={selectOptionsLimit}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Modular Blocks':
      return (
        <ModularBlocks
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          setCTOptions={setCTOptions}
        />
      )
    case 'Number':
      return (
        <Number
          type={type}
          options={options}
          isCTBuilder={isCTBuilder}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Boolean':
      return (
        <Boolean
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Date':
      return (
        <DateFieldProp
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          hasTimePickerConfig={hasTimePickerConfig}
          isUserDefinedZoneEnabled={isUserDefinedZoneEnabled}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'File':
      return (
        <File
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Group':
      return (
        <Group
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          hasNonLocFieldsWithinGroupOrGlobalField={hasNonLocFieldsWithinGroupOrGlobalField}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          setCTOptions={setCTOptions}
        />
      )

    case 'Link':
      return (
        <Link
          type={type}
          isCTBuilder={isCTBuilder}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          options={options}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Global':
      return (
        <Global
          type={type}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          hasNonLocFieldsWithinGroupOrGlobalField={hasNonLocFieldsWithinGroupOrGlobalField}
          options={options}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          globalFieldOptions={globalFieldOptions}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          setCTOptions={setCTOptions}
        />
      )
    case 'Custom':
      return (
        <Custom
          type={type}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          options={options}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          extensionFieldOption={extensionFieldOptions}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
        />
      )
    case 'ExperienceContainer':
      return (
        <ExperienceContainer
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          extensionFieldOption={extensionFieldOptions}
          t={t}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
        />
      )
    case 'Reference':
      return (
        <Reference
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          reference={referenceContentType}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
          contentTypeSchemaTree={contentTypeSchemaTree}
          isGlobalFieldBuilderPage={isGlobalFieldBuilderPage}
        />
      )
    case 'Taxonomy':
      return (
        <Taxonomy
          type={type}
          options={options}
          hasNonLocalizablePlan={hasNonLocalizablePlan}
          hasNonLocalizablePlanForBlocksAndGroupMultiple={hasNonLocalizablePlanForBlocksAndGroupMultiple}
          parentInfo={parentInfo}
          schema={schema}
          onChange={changehndlr}
          uniqueKey={uniqueKey}
          t={t}
          isNonLocalizable={isNonLocalizable || false}
          userCustomFieldLimits={{ hasUserCustomFieldLimit, hasUserCustomFieldLimitRestFields }}
        />
      )
    default:
      return null
  }
}

export const addFieldName = (schema: any, parentInfo: any) => {
  schema.fieldIdentifier = schema.fieldIdentifier || new Date().getTime()
  schema.fieldUid = parentInfo.parentFieldUid ? parentInfo.parentFieldUid + '.' + schema.uid : schema.uid
  if (schema.display_type) {
    schema.fieldName = 'Select'
  } else {
    if (schema.field_metadata && schema.field_metadata.extension) {
      schema.fieldName = 'Custom'
    } else {
      switch (schema.data_type) {
        case 'text':
          if (schema.field_metadata) {
            if (schema.field_metadata._default) {
              if (schema.uid === 'title') {
                schema.fieldName = 'Title'
              }
              if (schema.uid === 'url') {
                schema.fieldName = 'URL'
              }
            } else if (schema.field_metadata.multiline) {
              schema.fieldName = 'Multi Line Textbox'
            } else if (schema.field_metadata.allow_rich_text) {
              schema.fieldName = 'Rich Text Editor'
              schema.field_metadata = schema.field_metadata || {}
            } else if (schema.field_metadata.markdown) {
              schema.fieldName = 'Markdown'
            } else {
              schema.fieldName = 'Single Line Textbox'
            }
          }
          break
        case 'json':
          if (schema.field_metadata && schema.field_metadata.allow_json_rte) schema.fieldName = 'JSON Rich Text Editor'
          break
        case 'number':
          schema.fieldName = 'Number'
          break
        case 'isodate':
          schema.fieldName = 'Date'
          break
        case 'boolean':
          schema.fieldName = 'Boolean'
          break
        case 'reference':
          schema.fieldName = 'Reference'
          break
        case 'link':
          schema.fieldName = 'Link'
          break
        case 'file':
          schema.fieldName = 'File'
          if (schema.data_type == 'file') {
            if (schema.min && schema.min >= 701) {
              schema.min = parseFloat((schema.min / (1024 * 1024)).toFixed(3)) //Number((schema.min / (1024 * 1024)).toFixed(3))
            }
            if (schema.max && schema.max >= 701) {
              schema.max = parseFloat((schema.max / (1024 * 1024)).toFixed(3)) //Number((schema.max / (1024 * 1024)).toFixed(3))
            }
          }
          break
        case 'blocks':
          schema.fieldName = 'Modular Blocks'
          break
        case 'group':
          schema.fieldName = 'Group'
          if (schema.multiple) {
            schema.isInitialMultiple = true
          } else {
            schema.isInitialSingle = true
          }
          break
        case 'experience_container':
          schema.fieldName = 'ExperienceContainer'
          break
        case 'global_field':
          schema.fieldName = 'Global'
          if (schema.multiple) {
            schema.isInitialMultiple = true
          } else {
            schema.isInitialSingle = true
          }
          break
        case 'taxonomy':
          schema.fieldName = 'Taxonomy'
          break
      }
    }
  }
}

export const wrapperStyle = {
  top: '10px',
  left: 'auto !important',
  right: 0,
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  background: 'transparent !important'
}

export const fieldUidRegex = (uid: any, schema: any) => {
  const uidRegexErr = new RegExp('^[a-z][_a-z0-9]*$')
  schema.uid = uid
  return !uidRegexErr.test(schema.uid)
}

export const getContentTypeFieldDataTestId = (schema: any, ...testIds): string => {
  const fieldName: string = schema.fieldName || schema.field_name || ''
  const pageType = 'cs-content-type-field'
  return [pageType, getTestIdFormatter(fieldName), ...testIds].join('-')
}

export const generateUidFormat = (fieldName: string) => {
  let formattedFieldName = fieldName.toLowerCase();
  formattedFieldName = formattedFieldName.replace(/ /g, '-');
  return formattedFieldName
}

export const generateMinMaxTestId = (schema: any, fieldType: 'min' | 'max') => {
  const fieldName = generateUidFormat(schema.fieldName)
  return `cms-click-input-text-content-type-edit-${fieldName}-field-${fieldType}-limit`
}

export const CONTENT_TYPE_FIELD_ACTION_IDS = {
  DISPLAY_NAME_INPUT: 'display-name-input',
  DESCRIPTION_INPUT: 'description-input',
  HELP_TEXT_INPUT: 'help-text-input',
  UID_INPUT: 'uid-input',
  INSTRUCTION_INPUT: 'instruction-input',
  EXTENTION_TAG_AS_SELECT: 'extention-tag-as-select',
  REFERENCE_TAG_AS_SELECT: 'reference-tag-as-select',
  DATE_TEXT_INPUT: 'date-text-input',
  START_DATE_TEXT_INPUT: 'start-date-text-input',
  END_DATE_TEXT_INPUT: 'end-date-text-input',
  TIME_TEXT_INPUT: 'time-text-input',
  SET_MAX_LIMIT_INPUT: 'set-max-limit-input',
  DEFAULT_VARIATION_TOGGLE: 'default-variation-toggle',
  URL_PATTERN: 'url-pattern',
  CUSTOM_URL_INPUT: 'custom-url-input',
  MIN_FILE_SIZE_INPUT: 'min-file-size-input',
  MAX_FILE_SIZE_INPUT: 'max-file-size-input',
  ALLOW_FILE_TYPE_SELECT: 'allow-file-type-select',
  IMAGE_WIDTH_DROPDOWN: 'image-width-dropdown',
  MIN_IMAGE_WIDTH_INPUT: 'min-image-width-input',
  MAX_IMAGE_WIDTH_INPUT: 'max-image-width-input',
  EXACT_IMAGE_WIDTH_INPUT: 'exact-image-width-input',
  IMAGE_HEIGHT_DROPDOWN: 'image-height-dropdown',
  MIN_IMAGE_HEIGHT_INPUT: 'min-image-height-input',
  MAX_IMAGE_HEIGHT_INPUT: 'max-image-height-input',
  EXACT_IMAGE_HEIGHT_INPUT: 'exact-image-height-input',
  SELECT_GLOBAL_FIELD_TAG_AS_SELECT: 'select-global-field',
  SELECT_PLUGINS_TAG_AS_SELECT: 'select-plugins-tag-as-select',
  BASIC_EDITOR_TYPE_RADIO: 'basic-editor-type-radio',
  ADVANCED_EDITOR_TYPE_RADIO: 'advanced-editor-type-radio',
  CUSTOM_EDITOR_TYPE_RADIO: 'custom-editor-type-radio',
  FORMATTING_OPTIONS_SELECT_ALL_CHECKBOX: 'formatting-options-select-all-checkbox',
  LEARN_MORE: 'learn-more',
  OPTIONS_CHEKCBOX: 'options-checkbox',
  SELECT_OBJECTS_TAG_AS_SELECT: 'select-objects-tag-as-select',
  MANDATORY_CHEKCBOX: 'mandatory-checkbox',
  MULTIPLE_CHEKCBOX: 'multiple-checkbox',
  NON_LOCALIZABLE_CHEKCBOX: 'non-localizable-checkbox',
  TITLE_INPUT: 'title-input',
  URL_INPUT: 'url-input',
  PLACEHOLDER_INPUT: 'placeholder-input',
  MIN_NUMBER_OF_CHARACTERS_INPUT: 'min-number-of-characters-input',
  MAX_NUMBER_OF_CHARACTERS_INPUT: 'max-number-of-characters-input',
  VALIDATION_ERROR_MESSAGE_INPUT: 'validation-error-message-input',
  DEFAULT_VALUE_INPUT: 'default-value-input',
  VALIDATION_REGEX_INPUT: 'validation-regex-input',
  MIN_RANGE_INPUT: 'min-range-input',
  MAX_RANGE_INPUT: 'max-range-input',
  CHOICES_KEY_INPUT: 'choices-key-input',
  CHOICES_VALUE_INPUT: 'choices-value-input',
  CHOICES_INPUT: 'choices-input',
  DELETE_CHOICE_BUTTON: 'delete-choice-button',
  SELECTION_TYPE_DROPDOWN: 'selection-type-dropdown',
  DISPLAY_TYPE_DROPDOWN: 'display-type-dropdown',
  DISPLAY_HORIZONTAL_TOGGLE: 'display-horizontal-toggle',
  MIN_MULTIPLE_CHOICE_LIMIT_INPUT: 'min-multiple-choice-limit-input',
  MAX_MULTIPLE_CHOICE_LIMIT_INPUT: 'max-multiple-choice-limit-input',
  MIN_MULTIPLE_CHOICE_LIMIT_INPUT_VALID: 'min-multiple-choice-limit-input-valid',
  MAX_MULTIPLE_CHOICE_LIMIT_INPUT_VALID: 'max-multiple-choice-limit-input-valid',
  ADD_CHOICE_TOGGLE: 'add-choice-toggle',
  ADD_CHOICE_BUTTON: 'add-choice-button',
  FORMATTING_OPTIONS: 'formatting-options'
}

const HEIGHT_UPPER_RANGE = 600
const HEIGHT_LOWER_RANGE = 50

const resetValidationError = (schema, property) => {
  schema.error_details = {
    ...schema.error_details,
    [property]: ''
  }
  schema.error = Object.keys(schema.error_details).filter((fi) => schema.error_details[fi].length).length
    ? true
    : false
}

export const validateMinHeight = (minHeight: number | null, maxHeightNum: number | null, schema, onlyReset: boolean = false) => {
  const allowedMaxHeight = maxHeightNum || HEIGHT_UPPER_RANGE;
  const isMinHeightOutsideLowerRange = minHeight && minHeight < HEIGHT_LOWER_RANGE;
  const isMinHeightOutsideUpperRange = minHeight && minHeight > allowedMaxHeight;
  const isMinHeightOutsideAnyRange = isMinHeightOutsideLowerRange || isMinHeightOutsideUpperRange
  const isValid = minHeight === null || !isMinHeightOutsideAnyRange
  if (isValid) {
    resetValidationError(schema, 'min_height')
  }
  if (!onlyReset && !isValid) {
    schema.error_details.min_height = isMinHeightOutsideLowerRange
      ? 'minimum height cannot be less than 50'
      : `minimum height cannot be greater than ${maxHeightNum ? 'maximum height' : HEIGHT_UPPER_RANGE}`;
    schema.error = true;
  }
};

export const validateMaxHeight = (minHeightNum: number | null, maxHeight: number | null, schema, onlyReset: boolean = false) => {
  const allowedMinHeight = minHeightNum || HEIGHT_LOWER_RANGE;
  const isMaxHeightOutsideLowerRange = maxHeight && maxHeight < allowedMinHeight;
  const isMaxHeightOutsideUpperRange = maxHeight && maxHeight > HEIGHT_UPPER_RANGE;
  const isMaxHeightOutsideAnyRange = isMaxHeightOutsideLowerRange || isMaxHeightOutsideUpperRange
  const isValid = maxHeight === null || !isMaxHeightOutsideAnyRange
  if (isValid) {
    resetValidationError(schema, 'max_height');
  }
  if (!onlyReset && !isValid) {
    schema.error_details.max_height = isMaxHeightOutsideUpperRange
      ? 'maximum height cannot be greater than 600'
      : `maximum height cannot be less than ${minHeightNum ? 'minimum height' : HEIGHT_LOWER_RANGE}`;
    schema.error = true;
  }
};

export const ParentNonLocInfoModal = (props) => {

  const { fieldType, onChange, onContinue } = props;
  const fieldTypeMap = {
    'blocks': 'Modular Blocks',
    'group': 'Group',
    // 'global_field': 'Global Field'
  }
  const fieldDataType = fieldTypeMap[fieldType] || fieldType;
  return (
    <div data-test-id="cs-parent-non-localizable-modal">
      <ModalHeader title={'Update to Non-Localizable Behavior'} closeModal={props.closeModal} />
      <ModalBody>
        <div>{`You are about to mark the entire ${fieldDataType} field as non-localizable.`}<br />With this action:</div>
        <div className="mt-20"> <Icon icon="Bullet" size='mini' /> All fields inside the {fieldDataType} will be non-localizable. <br />
          <Icon icon="Bullet" size='mini' /> The Non Localizable icons for individually marked fields will be removed to reflect this action. <br />
          <Icon icon="Bullet" size='mini' /> Localization behavior will now be controlled entirely at the {fieldDataType} level.</div>
        <div className='mt-20'> {`This change ensures that all data inside the ${fieldDataType} comes from the master locale and is not editable in localized entries.`} </div>
      </ModalBody>
      <ModalFooter>
        <ButtonGroup>
          <Button testId="cs-parent-non-localizable-modal-cancel" onClick={props.closeModal} version="v2" buttonType="tertiary">Cancel</Button>
          <Button
            onClick={(): void => {
              onContinue();
              onChange()
              props.closeModal()
            }}
            version="v2"
            testId="cs-parent-non-localizable-modal-continue"
          >
            Continue
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </div>
  )
}

export const handleMarkParentNonLocalizable = (schema, onChange) => {
  cbModal({
    component: (modalProps) => (
      <ParentNonLocInfoModal
        {...modalProps}
        fieldType={schema.data_type}
        onChange={onChange}
        onContinue={() => {
          schema.non_localizable = true;
          markNonLocalizableFalse(schema);
        }}
      />
    ),
    modalProps: {
      className: 'non-localizable-parent-info-modal'
    },
  });
};


export const handleTabToggle = (event, schema) => {
  if (event.target.checked) {
    if(!schema.field_metadata) schema.field_metadata = {};
    schema.field_metadata['isTab'] = true;
  } else {
    delete schema.field_metadata?.isTab
  }
}

export const ToggleSwitchTab = ({uniqueKey , isTab , onOptionsChange , fieldType , t}) => {
  return (
    <div className={`ToggleWrap flex-v-center`}>
    <ToggleSwitch
      testId={`cs-ct-field-${fieldType}-tab-${isTab ? 'enabled' : 'disabled'}`}
      name={`${uniqueKey}.tab`}
      label="Show as Tab"
      checked={isTab}
      onClick={onOptionsChange}
    />
      <div>
        <Help type="basic" className='mb-0 ml-8' iconName='Information' text={t('content_type.save_content_type.body.fields.tooltip_text.tab') || "Displays this field as a separate tab in the entry editor. Available only for root-level Groups, Modular Blocks, and Global fields."} />
      </div>
  </div>
  )
}
